import { useState, useEffect, createContext, useContext, ReactNode, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type Theme = 'dark' | 'clean';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'velloplay-theme';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY) as Theme;
      if (stored === 'dark' || stored === 'clean') return stored;
      // Migrate old 'light' theme to 'clean'
      if (stored === 'light') return 'clean';
    }
    return 'dark'; // Default to dark mode
  });

  const [userId, setUserId] = useState<string | null>(null);

  // Apply theme class to document
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark', 'clean');
    root.classList.add(theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  // Listen to auth state changes to get user ID
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUserId(session?.user?.id ?? null);
    });

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch user's preferred theme when authenticated
  useEffect(() => {
    if (!userId) return;

    const fetchUserTheme = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('preferred_theme')
        .eq('id', userId)
        .single();
      
      if (data?.preferred_theme && (data.preferred_theme === 'dark' || data.preferred_theme === 'clean')) {
        setThemeState(data.preferred_theme as Theme);
      }
    };

    fetchUserTheme();
  }, [userId]);

  // Save theme to profile when changed (for authenticated users)
  const persistTheme = useCallback(async (newTheme: Theme) => {
    if (!userId) return;
    
    await supabase
      .from('profiles')
      .update({ preferred_theme: newTheme })
      .eq('id', userId);
  }, [userId]);

  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'dark' ? 'clean' : 'dark';
    setThemeState(newTheme);
    persistTheme(newTheme);
  }, [theme, persistTheme]);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    persistTheme(newTheme);
  }, [persistTheme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
